/**
 * localOverrides.js
 * =================
 * Manages compound overrides stored in localStorage.
 * These are applied ON TOP of the compiled compoundsDB at runtime,
 * so no rebuild is ever needed for day-to-day data edits.
 *
 * Storage key: "wohler_overrides"
 * Format: { "compound-id": { field: value, ... }, ... }
 */

const STORAGE_KEY = 'wohler_overrides';

/** Load all overrides from localStorage. Returns {} if none. */
export function loadOverrides() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    console.warn('[wohler] Could not load overrides from localStorage', e);
    return {};
  }
}

/** Save the full overrides object to localStorage. */
export function saveOverrides(overrides) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
    return true;
  } catch (e) {
    console.warn('[wohler] Could not save overrides to localStorage', e);
    return false;
  }
}

/** Merge a batch of updates into the existing overrides and persist. */
export function mergeOverrides(updates) {
  const existing = loadOverrides();
  const merged = {};
  // Deep-merge per compound
  const allIds = new Set([...Object.keys(existing), ...Object.keys(updates)]);
  allIds.forEach(id => {
    merged[id] = { ...(existing[id] || {}), ...(updates[id] || {}) };
  });
  saveOverrides(merged);
  return merged;
}

/** Clear all overrides from localStorage. */
export function clearOverrides() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {}
}

/** Return count of overridden compounds and total fields changed. */
export function getOverrideStats() {
  const ov = loadOverrides();
  const compounds = Object.keys(ov).length;
  const fields = Object.values(ov).reduce((n, obj) => n + Object.keys(obj).length, 0);
  return { compounds, fields };
}

/**
 * Apply overrides on top of a compoundsDB snapshot.
 * Returns a new object — does not mutate the original.
 */
export function applyOverrides(db, overrides) {
  if (!overrides || Object.keys(overrides).length === 0) return db;
  const result = { ...db };
  Object.entries(overrides).forEach(([id, fields]) => {
    if (result[id]) {
      result[id] = { ...result[id], ...fields };
    }
    // If the id doesn't exist in the DB yet, still add it
    // (supports adding brand-new compounds via Excel)
    else if (fields.id && fields.name) {
      result[id] = { ...fields };
    }
  });
  return result;
}
